import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getClientBookings, getClientInquiries } from '../../../services/firestore/bookings';
import { useAuth } from '../../../contexts/AuthContext';
import { Package, MapPin, Calendar, ArrowRight, MessageSquare } from 'lucide-react';

const MyBookings = () => {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('bookings'); // 'bookings' or 'inquiries'

  useEffect(() => {
    const loadData = async () => {
      if (!user) return;
      try {
        const [bookingsData, inquiriesData] = await Promise.all([
          getClientBookings(user.uid),
          getClientInquiries(user.uid).catch(() => [])
        ]);
        setBookings(bookingsData);
        setInquiries(inquiriesData);
      } catch (error) {
        console.error('Error loading bookings:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-emerald-50/20 via-white to-green-50/10">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-emerald-600 border-t-transparent"></div>
      </div>
    );
  }

  const displayItems = activeTab === 'bookings' ? bookings : inquiries;
  const isEmpty = displayItems.length === 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50/20 via-white to-green-50/10 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-light text-gray-900 mb-2">My Trips</h1>
          <p className="text-lg text-gray-600 font-light">
            View and manage your booked trips and inquiries
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('bookings')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'bookings'
                  ? 'border-emerald-500 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4" />
                <span>Bookings ({bookings.length})</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('inquiries')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'inquiries'
                  ? 'border-emerald-500 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                <span>Inquiries ({inquiries.length})</span>
              </div>
            </button>
          </nav>
        </div>

        {isEmpty ? (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-12 text-center">
            {activeTab === 'bookings' ? (
              <>
                <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-medium text-gray-900 mb-2">No trips booked yet</h3>
                <p className="text-gray-600 mb-6">Start exploring amazing trips and book your first experience.</p>
              </>
            ) : (
              <>
                <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-medium text-gray-900 mb-2">No inquiries yet</h3>
                <p className="text-gray-600 mb-6">Send inquiries to hosts to learn more about their trips.</p>
              </>
            )}
            <Link
              to="/client/trips"
              className="inline-flex items-center gap-2 group bg-emerald-700 hover:bg-emerald-800 text-white px-6 py-3 rounded-lg text-base font-medium shadow-lg shadow-emerald-200/50 hover:shadow-xl hover:shadow-emerald-300/50 transition-all duration-300"
            >
              Browse Available Trips
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {displayItems.map((item) => {
              const isInquiry = item.type === 'inquiry';
              const selectedDate = item.selectedDate 
                ? (item.selectedDate?.toDate ? item.selectedDate.toDate() : new Date(item.selectedDate))
                : null;

              return (
                <Link
                  key={item.id}
                  to={`/client/bookings/${item.id}`}
                  className="block group bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 p-6 hover:shadow-xl hover:border-emerald-200 transition-all duration-300"
                >
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {isInquiry && <MessageSquare className="w-4 h-4 text-emerald-600" />}
                        <h3 className="text-lg font-medium text-gray-900 group-hover:text-emerald-700 transition-colors">
                          {item.tripTitle || `${isInquiry ? 'Inquiry' : 'Booking'} #${item.id?.substring(0, 8)}`}
                        </h3>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            item.status === 'confirmed'
                              ? 'bg-emerald-100 text-emerald-800'
                              : item.status === 'pending'
                              ? 'bg-amber-100 text-amber-800'
                              : item.status === 'canceled'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {item.status}
                        </span>
                      </div>
                      {item.tripLocation && (
                        <div className="flex items-center gap-2 text-gray-600 mb-1">
                          <MapPin className="w-4 h-4 text-emerald-600" />
                          <span className="text-sm">{item.tripLocation}</span>
                        </div>
                      )}
                      {selectedDate && (
                        <div className="flex items-center gap-2 text-gray-600 mb-1">
                          <Calendar className="w-4 h-4 text-emerald-600" />
                          <span className="text-sm">
                            Arrival: {selectedDate.toLocaleDateString('en-US', { 
                              weekday: 'long', 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}
                          </span>
                        </div>
                      )}
                      {item.guestCount && (
                        <p className="text-sm text-gray-600 mb-1">
                          {item.guestCount} {item.guestCount === 1 ? 'guest' : 'guests'}
                        </p>
                      )}
                      {item.totalAmount && (
                        <p className="text-lg font-bold text-emerald-700 mt-2">
                          ${typeof item.totalAmount === 'number' ? item.totalAmount.toFixed(2) : item.totalAmount}
                        </p>
                      )}
                      {item.message && (
                        <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                          {item.message}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-emerald-700 group-hover:text-emerald-800 transition-colors">
                      <span className="text-sm font-medium">View Details</span>
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyBookings;

